import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable'; // Import correct
import dayjs from 'dayjs';
import { displayNumbers } from '../../../../variables/numbersFunctions';

// =====================================================
// EXPORT EXCEL
// =====================================================

export const exportSAPToExcel = (
    statistiquesGlobales,
    detailSinistres,
    resumeParBeneficiaire,
    compagniesBeneficiaires,
    filters
) => {
    const workbook = XLSX.utils.book_new();
    const { exercice, categorie, compagnie } = filters;

    // ===== FEUILLE 1: Résumé Global =====
    const resumeGlobalData = [
        ['STATISTIQUES SAP - RÉSUMÉ GLOBAL'],
        [`Exercice: ${exercice || 'Tous'}`, `Catégorie: ${categorie || 'Toutes'}`, `Compagnie: ${compagnie || 'Toutes'}`],
        [`Généré le: ${dayjs().format('DD/MM/YYYY HH:mm')}`],
        [],
        // En-têtes
        [
            'Exercice',
            'Compagnie Cédante',
            'Code',
            'Catégorie',
            'Nbre Sinistres',
            'SAP Total',
            ...compagniesBeneficiaires.map(c => `${c.nom} (${(c.taux || 0).toFixed(2)}%)`)
        ]
    ];

    // Données
    let totalSinistres = 0;
    let totalSap = 0;
    const totauxParBeneficiaire = {};
    compagniesBeneficiaires.forEach(c => { totauxParBeneficiaire[c.code] = 0; });

    statistiquesGlobales.forEach(row => {
        totalSinistres += row.nombreSinistres || 0;
        totalSap += row.montantTotalSap || 0;

        const benefMontants = compagniesBeneficiaires.map(comp => {
            const rep = row.repartitionParBeneficiaire?.find(r => r.beneficiaireCode === comp.code);
            const montant = rep?.montantSAP || 0;
            totauxParBeneficiaire[comp.code] += montant;
            return montant;
        });

        resumeGlobalData.push([
            row.exercice,
            row.compagnieCedanteNom,
            row.compagnieCedanteCode,
            row.categorieCode,
            row.nombreSinistres,
            row.montantTotalSap,
            ...benefMontants
        ]);
    });

    // Ligne de total
    resumeGlobalData.push([]);
    resumeGlobalData.push([
        'TOTAL',
        '',
        '',
        '',
        totalSinistres,
        totalSap,
        ...compagniesBeneficiaires.map(c => totauxParBeneficiaire[c.code])
    ]);

    const wsResumeGlobal = XLSX.utils.aoa_to_sheet(resumeGlobalData);

    // Largeurs de colonnes
    wsResumeGlobal['!cols'] = [
        { wch: 10 }, { wch: 25 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 18 },
        ...compagniesBeneficiaires.map(() => ({ wch: 18 }))
    ];

    XLSX.utils.book_append_sheet(workbook, wsResumeGlobal, 'Résumé Global');

    // ===== FEUILLE 2: Détail des Sinistres =====
    const detailData = [
        ['DÉTAIL DES SINISTRES SAP'],
        [`Exercice: ${exercice || 'Tous'}`, `Catégorie: ${categorie || 'Toutes'}`, `Compagnie: ${compagnie || 'Toutes'}`],
        [`Généré le: ${dayjs().format('DD/MM/YYYY HH:mm')}`],
        [],
        // En-têtes
        [
            'N° Sinistre',
            'Date de survenance',
            'N° Police',
            'Souscripteur',
            'Compagnie Cédante',
            'Catégorie',
            'Statut',
            'Évaluation',
            'Réglé',
            'SAP',
            ...compagniesBeneficiaires.map(c => c.nom)
        ]
    ];

    detailSinistres.forEach(sin => {
        const benefMontants = compagniesBeneficiaires.map(comp => {
            const rep = sin.repartition?.find(r => r.beneficiaireCode === comp.code);
            return rep?.montantSAP || 0;
        });

        detailData.push([
            sin.numeroSinistre,
            sin.dateSinistre ? dayjs(sin.dateSinistre).format('DD/MM/YYYY') : '',
            sin.numeroPolice,
            sin.souscripteur,
            sin.compagnieCedanteNom,
            sin.categorieCode,
            sin.statutSinistre,
            sin.evaluation,
            sin.montantRegle,
            sin.sap,
            ...benefMontants
        ]);
    });

    // Totaux
    const totalEval = detailSinistres.reduce((sum, s) => sum + (s.evaluation || 0), 0);
    const totalRegle = detailSinistres.reduce((sum, s) => sum + (s.montantRegle || 0), 0);
    const totalSapDetail = detailSinistres.reduce((sum, s) => sum + (s.sap || 0), 0);

    const totauxDetailBenef = compagniesBeneficiaires.map(comp => {
        return detailSinistres.reduce((sum, sin) => {
            const rep = sin.repartition?.find(r => r.beneficiaireCode === comp.code);
            return sum + (rep?.montantSAP || 0);
        }, 0);
    });

    detailData.push([]);
    detailData.push([
        'TOTAL',
        '',
        '',
        '',
        '',
        '',
        `${detailSinistres.length} sinistres`,
        totalEval,
        totalRegle,
        totalSapDetail,
        ...totauxDetailBenef
    ]);

    const wsDetail = XLSX.utils.aoa_to_sheet(detailData);
    wsDetail['!cols'] = [
        { wch: 15 }, { wch: 12 }, { wch: 15 }, { wch: 25 }, { wch: 20 },
        { wch: 10 }, { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
        ...compagniesBeneficiaires.map(() => ({ wch: 15 }))
    ];
    XLSX.utils.book_append_sheet(workbook, wsDetail, 'Détail Sinistres');

    // ===== FEUILLE 3: Résumé par Bénéficiaire =====
    const resumeBenefData = [
        ['RÉSUMÉ SAP PAR COMPAGNIE BÉNÉFICIAIRE'],
        [`Exercice: ${exercice || 'Tous'}`, `Catégorie: ${categorie || 'Toutes'}`],
        [`Généré le: ${dayjs().format('DD/MM/YYYY HH:mm')}`],
        [],
        // En-têtes
        [
            'Exercice',
            'Compagnie Bénéficiaire',
            'Code',
            'Catégorie',
            'Taux (%)',
            'Nbre Sinistres',
            'SAP Réparti'
        ]
    ];

    let totalSapBenef = 0;
    resumeParBeneficiaire.forEach(row => {
        totalSapBenef += row.montantSapReparti || 0;
        resumeBenefData.push([
            row.exercice,
            row.beneficiaireNom,
            row.beneficiaireCode,
            row.categorieCode,
            row.tauxRepartition,
            row.nombreSinistres,
            row.montantSapReparti
        ]);
    });

    resumeBenefData.push([]);
    resumeBenefData.push([
        'TOTAL',
        '',
        '',
        '',
        '',
        '',
        totalSapBenef
    ]);

    const wsResumeBenef = XLSX.utils.aoa_to_sheet(resumeBenefData);
    wsResumeBenef['!cols'] = [
        { wch: 10 }, { wch: 30 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 18 }
    ];
    XLSX.utils.book_append_sheet(workbook, wsResumeBenef, 'Par Bénéficiaire');

    // ===== FEUILLE 4: Détail par Cédante (pour chaque bénéficiaire) =====
    compagniesBeneficiaires.forEach(benef => {
        const dataForBenef = resumeParBeneficiaire.filter(r => r.beneficiaireCode === benef.code);

        if (dataForBenef.length > 0) {
            const sheetData = [
                [`SAP RÉPARTI - ${benef.nom}`],
                [`Taux de répartition: ${(benef.taux || 0).toFixed(2)}%`],
                [`Généré le: ${dayjs().format('DD/MM/YYYY HH:mm')}`],
                [],
                ['Exercice', 'Catégorie', 'Compagnie Cédante', 'Nbre Sinistres', 'SAP Réparti']
            ];

            let totalBenef = 0;
            dataForBenef.forEach(row => {
                row.detailParCedante?.forEach(detail => {
                    totalBenef += detail.montantSAP || 0;
                    sheetData.push([
                        row.exercice,
                        row.categorieCode,
                        detail.cedanteNom,
                        detail.nombreSinistres,
                        detail.montantSAP
                    ]);
                });
            });

            sheetData.push([]);
            sheetData.push(['TOTAL', '', '', '', totalBenef]);

            const ws = XLSX.utils.aoa_to_sheet(sheetData);
            ws['!cols'] = [{ wch: 10 }, { wch: 10 }, { wch: 25 }, { wch: 12 }, { wch: 18 }];

            // Nom de feuille limité à 31 caractères
            const sheetName = benef.code.substring(0, 31);
            XLSX.utils.book_append_sheet(workbook, ws, sheetName);
        }
    });

    // Générer le fichier
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const fileName = `SAP_Statistiques_${exercice || 'Tous'}_${dayjs().format('YYYYMMDD_HHmm')}.xlsx`;
    saveAs(data, fileName);
};


// =====================================================
// EXPORT PDF
// =====================================================

export const exportSAPToPDF = (
    statistiquesGlobales,
    detailSinistres,
    resumeParBeneficiaire,
    compagniesBeneficiaires,
    filters,
    totaux
) => {
    const { exercice, categorie, compagnie } = filters;

    // Créer le document PDF en mode paysage
    const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let currentY = 15;

    // Fonction pour ajouter l'en-tête
    const addHeader = (title) => {
        doc.setFillColor(102, 126, 234);
        doc.rect(0, 0, pageWidth, 25, 'F');

        doc.setTextColor(255, 255, 255);
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text(title, 14, 12);

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`Exercice: ${exercice || 'Tous'} | Catégorie: ${categorie || 'Toutes'} | Compagnie: ${compagnie || 'Toutes'}`, 14, 20);

        doc.setTextColor(0, 0, 0);
        currentY = 35;
    };

    // Fonction pour ajouter le pied de page
    const addFooter = (pageNum, totalPages) => {
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(
            `Page ${pageNum} / ${totalPages} - Généré le ${dayjs().format('DD/MM/YYYY à HH:mm')}`,
            pageWidth / 2,
            pageHeight - 10,
            { align: 'center' }
        );
    };

    // ===== PAGE 1: Résumé Global =====
    addHeader('STATISTIQUES SAP - RÉSUMÉ GLOBAL');

    // Cartes de résumé
    const cardWidth = 55;
    const cardHeight = 25;
    const cardStartX = 14;
    const cards = [
        { label: 'Nombre de sinistres', value: totaux.nombreSinistres, color: [24, 144, 255] },
        { label: 'SAP Total', value: `${displayNumbers(totaux.montantSap)} F`, color: [255, 77, 79] },
        { label: 'Évaluation totale', value: `${displayNumbers(totaux.montantEvaluation)} F`, color: [82, 196, 26] },
        { label: 'Montant réglé', value: `${displayNumbers(totaux.montantRegle)} F`, color: [114, 46, 209] }
    ];

    cards.forEach((card, index) => {
        const x = cardStartX + (index * (cardWidth + 5));

        doc.setFillColor(...card.color);
        doc.roundedRect(x, currentY, cardWidth, cardHeight, 3, 3, 'F');

        doc.setTextColor(255, 255, 255);
        doc.setFontSize(9);
        doc.text(card.label, x + 5, currentY + 8);

        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(String(card.value), x + 5, currentY + 18);
        doc.setFont('helvetica', 'normal');
    });

    currentY += cardHeight + 15;
    doc.setTextColor(0, 0, 0);

    // Tableau résumé global
    const resumeHeaders = [
        'Exercice',
        'Compagnie Cédante',
        'Cat.',
        'Nbre Sin.',
        'SAP Total',
        ...compagniesBeneficiaires.slice(0, 4).map(c => c.code) // Limiter pour la place
    ];

    const resumeData = statistiquesGlobales.slice(0, 15).map(row => {
        const benefValues = compagniesBeneficiaires.slice(0, 4).map(comp => {
            const rep = row.repartitionParBeneficiaire?.find(r => r.beneficiaireCode === comp.code);
            return displayNumbers(rep?.montantSAP || 0);
        });

        return [
            row.exercice,
            row.compagnieCedanteNom?.substring(0, 20),
            row.categorieCode,
            row.nombreSinistres,
            displayNumbers(row.montantTotalSap),
            ...benefValues
        ];
    });

    autoTable(doc, {
        startY: currentY,
        head: [resumeHeaders],
        body: resumeData,
        theme: 'striped',
        headStyles: {
            fillColor: [102, 126, 234],
            textColor: 255,
            fontSize: 8,
            fontStyle: 'bold'
        },
        bodyStyles: {
            fontSize: 8
        },
        columnStyles: {
            0: { cellWidth: 18 },
            1: { cellWidth: 45 },
            2: { cellWidth: 15 },
            3: { cellWidth: 18, halign: 'center' },
            4: { cellWidth: 30, halign: 'right' }
        },
        margin: { left: 14, right: 14 }
    });

    // ===== PAGE 2: Répartition par Bénéficiaire =====
    doc.addPage();
    addHeader('RÉPARTITION SAP PAR COMPAGNIE BÉNÉFICIAIRE');

    // Graphique simplifié (barres horizontales)
    const maxSap = Math.max(...compagniesBeneficiaires.map(comp => {
        return resumeParBeneficiaire
            .filter(r => r.beneficiaireCode === comp.code)
            .reduce((sum, r) => sum + (r.montantSapReparti || 0), 0);
    }));

    const barStartX = 70;
    const barMaxWidth = 150;
    const barHeight = 12;
    const barSpacing = 18;
    const colors = [[24, 144, 255], [82, 196, 26], [250, 173, 20], [255, 77, 79], [114, 46, 209], [19, 194, 194]];

    compagniesBeneficiaires.forEach((comp, index) => {
        const totalBenef = resumeParBeneficiaire
            .filter(r => r.beneficiaireCode === comp.code)
            .reduce((sum, r) => sum + (r.montantSapReparti || 0), 0);

        const barWidth = maxSap > 0 ? (totalBenef / maxSap) * barMaxWidth : 0;
        const y = currentY + (index * barSpacing);
        const color = colors[index % colors.length];

        // Label
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        doc.text(comp.nom.substring(0, 15), 14, y + 8);

        // Barre
        doc.setFillColor(...color);
        doc.roundedRect(barStartX, y, barWidth, barHeight, 2, 2, 'F');

        // Valeur
        doc.setFontSize(8);
        doc.text(`${displayNumbers(totalBenef)} F`, barStartX + barWidth + 5, y + 8);
    });

    currentY += compagniesBeneficiaires.length * barSpacing + 20;

    // Tableau par bénéficiaire
    const benefHeaders = ['Bénéficiaire', 'Catégorie', 'Taux (%)', 'Nbre Sin.', 'SAP Réparti'];
    const benefData = resumeParBeneficiaire.slice(0, 20).map(row => [
        row.beneficiaireNom?.substring(0, 25),
        row.categorieCode,
        (row.tauxRepartition || 0).toFixed(2),
        row.nombreSinistres,
        displayNumbers(row.montantSapReparti)
    ]);

    autoTable(doc, {
        startY: currentY,
        head: [benefHeaders],
        body: benefData,
        theme: 'striped',
        headStyles: {
            fillColor: [114, 46, 209],
            textColor: 255,
            fontSize: 9,
            fontStyle: 'bold'
        },
        bodyStyles: {
            fontSize: 8
        },
        columnStyles: {
            0: { cellWidth: 60 },
            1: { cellWidth: 25 },
            2: { cellWidth: 25, halign: 'center' },
            3: { cellWidth: 25, halign: 'center' },
            4: { cellWidth: 40, halign: 'right' }
        },
        margin: { left: 14, right: 14 }
    });

    // ===== PAGE 3: Détail des sinistres (résumé) =====
    doc.addPage();
    addHeader('DÉTAIL DES SINISTRES SAP');

    const detailHeaders = ['N° Sinistre', 'Date', 'N° Police', 'Souscripteur', 'Cédante', 'Cat.', 'SAP'];
    const detailData = detailSinistres.slice(0, 25).map(sin => [
        sin.numeroSinistre?.substring(0, 15),
        sin.dateSinistre ? dayjs(sin.dateSinistre).format('DD/MM/YY') : '',
        sin.numeroPolice?.substring(0, 15),
        sin.souscripteur?.substring(0, 20),
        sin.compagnieCedanteCode,
        sin.categorieCode,
        displayNumbers(sin.sap)
    ]);

    autoTable(doc, {
        startY: currentY,
        head: [detailHeaders],
        body: detailData,
        theme: 'striped',
        headStyles: {
            fillColor: [255, 77, 79],
            textColor: 255,
            fontSize: 9,
            fontStyle: 'bold'
        },
        bodyStyles: {
            fontSize: 8
        },
        columnStyles: {
            0: { cellWidth: 35 },
            1: { cellWidth: 22 },
            2: { cellWidth: 35 },
            3: { cellWidth: 50 },
            4: { cellWidth: 25 },
            5: { cellWidth: 15 },
            6: { cellWidth: 30, halign: 'right' }
        },
        margin: { left: 14, right: 14 }
    });

    // Note si données tronquées
    if (detailSinistres.length > 25) {
        const finalY = doc.lastAutoTable.finalY + 10;
        doc.setFontSize(9);
        doc.setTextColor(128, 128, 128);
        doc.text(
            `* Affichage limité à 25 sinistres sur ${detailSinistres.length}. Voir l'export Excel pour la liste complète.`,
            14,
            finalY
        );
    }

    // Ajouter les pieds de page
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        addFooter(i, totalPages);
    }

    // Sauvegarder le PDF
    const fileName = `SAP_Statistiques_${exercice || 'Tous'}_${dayjs().format('YYYYMMDD_HHmm')}.pdf`;
    doc.save(fileName);
};


// =====================================================
// IMPRESSION DIRECTE
// =====================================================

export const printSAPReport = (
    statistiquesGlobales,
    detailSinistres,
    resumeParBeneficiaire,
    compagniesBeneficiaires,
    filters,
    totaux
) => {
    // Générer le PDF et l'ouvrir dans une nouvelle fenêtre pour impression
    const { exercice, categorie, compagnie } = filters;

    const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
    });

    // ... (même contenu que exportSAPToPDF)
    // Pour simplifier, on peut réutiliser la même logique

    // Ouvrir pour impression
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    const printWindow = window.open(pdfUrl);

    if (printWindow) {
        printWindow.onload = () => {
            printWindow.print();
        };
    }
};